import { useMotionValue, useTransform } from 'framer-motion';
import { useCallback, useEffect, useRef } from 'react';
import { useContentElement } from '@polestar/component-warehouse-react/contexts/contentElement';
import ResizeObserver from 'resize-observer-polyfill';
import { clamp } from '@polestar/component-warehouse-react/helpers/mathematical';
import { useParallaxRange } from '@polestar/component-warehouse-react/components/parallax-range/ParallaxRangeContext';
/**
 * Use scroll progress hook
 */
const useScrollProgress = (props) => {
    const scrollProgressRef = useRef(null);
    const scrollOffset = useMotionValue(0);
    const sectionHeight = useMotionValue(0);
    const { scrollProgressRef: rangeScrollProgressRef } = useParallaxRange();
    // This can be moved to page wide context
    const { contentRef, windowHeight, scrollY } = useContentElement();
    /**
     * Element top @ bottom of the viewport = 0
     * Element bottom @ top of the viewport = 1
     */
    const progress = useTransform(
    // @ts-ignore
    [scrollY, scrollOffset, sectionHeight, windowHeight], 
    // @ts-ignore
    ([latestScrollY, latestScrollOffset, latestSectionHeight, latestWindowHeight]) => {
        const componentScrollRange = latestWindowHeight + latestSectionHeight;
        // prevent SSR hydration issues when it is known that an element is at a certain position
        if ((props === null || props === void 0 ? void 0 : props.initialValue) && windowHeight.get() === 0) {
            return props === null || props === void 0 ? void 0 : props.initialValue;
        }
        return clamp((latestScrollY + latestWindowHeight - latestScrollOffset) / componentScrollRange || 0, 0, 1);
    });
    /**
     * Element top @ top the viewport = 0
     * Element bottom @ bottom of the viewport = 1
     */
    const outerProgress = useTransform([scrollY, scrollOffset, sectionHeight, windowHeight], ([latestScrollY, latestScrollOffset, latestSectionHeight, latestWindowHeight]) => {
        const componentScrollRange = latestSectionHeight - latestWindowHeight;
        return clamp((latestScrollY - latestScrollOffset) / componentScrollRange || 0, 0, 1);
    });
    /**
     * Recalculate needed offset
     */
    const updateCalculationValues = useCallback(() => {
        if (scrollProgressRef.current) {
            scrollOffset.set(rangeScrollProgressRef.current
                ? getOffsetTop(rangeScrollProgressRef.current)
                : getOffsetTop(scrollProgressRef.current));
            sectionHeight.set(scrollProgressRef.current.offsetHeight);
        }
    }, [rangeScrollProgressRef, scrollOffset, sectionHeight]);
    useEffect(() => {
        // Trigger on initialize
        updateCalculationValues();
        if (contentRef === null || contentRef === void 0 ? void 0 : contentRef.current) {
            const resizeObserver = new ResizeObserver(() => {
                updateCalculationValues();
            });
            resizeObserver.observe(contentRef.current);
            return () => {
                resizeObserver.disconnect();
            };
        }
    }, [updateCalculationValues, contentRef]);
    /**
     * Keep track of the progress direction
     */
    const direction = useMotionValue(1);
    useEffect(() => {
        let previousProgress = -1;
        return progress.on('change', (value) => {
            direction.set(previousProgress < value ? 1 : -1);
            previousProgress = value;
        });
    }, [direction, progress]);
    if ((props === null || props === void 0 ? void 0 : props.initialValue) && windowHeight.get() === 0) {
        // helps with hydration issues until the window height is initialized
        progress.set(props === null || props === void 0 ? void 0 : props.initialValue);
    }
    return { scrollProgressRef, progress, direction, outerProgress, updateCalculationValues };
};
export default useScrollProgress;
/**
 * We use this because this ignores the transform on the element
 */
const getOffsetTop = (element) => {
    let top = 0;
    while (element) {
        top += element.offsetTop;
        element = element.offsetParent;
    }
    return top;
};
